import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import { deleteSubscriptionUsage } from "@/store/modules/subscriptionUsage/thunks";
import { useAuth } from "@/context/AuthContext";

interface UseDeleteSubscriptionUsageProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const useDeleteSubscriptionUsage = ({
  onSuccess,
  onError,
}: UseDeleteSubscriptionUsageProps = {}) => {
  const dispatch: AppDispatch = useDispatch();
  const { deleting, error } = useSelector(
    (state: RootState) => state.subscriptionUsage,
  );
  const { establishment } = useAuth();

  const handleDelete = async (
    establishmentId: string | number | undefined,
    subscriptionId?: string | number | undefined,
    clientSubscriptionId?: string | number | undefined,
    usageId?: string | number,
  ) => {
    try {
      const payload: any = {
        establishmentId,
        subscriptionId,
        clientSubscriptionId,
        usageId,
      };
      await dispatch(deleteSubscriptionUsage(payload) as any).unwrap();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      if (onError) onError(err?.message || "Erro ao excluir utilização.");
    }
  };

  const handleDeleteUsage = async (usage: any) => {
    // Tentar extrair ids de várias formas possíveis para ser robusto contra formatos diferentes
    const establishmentId =
      usage.establishment_id ||
      usage.establishment?.id ||
      usage.establishment?.slug ||
      usage.establishment_slug;

    const usageId = usage.id || usage.usage_id || usage.usageId;

    if (!usageId) {
      if (onError)
        onError("Não foi possível determinar usage id para exclusão");
      return;
    }

    const establishmentSlug =
      usage.establishment?.slug ||
      usage.establishment_slug ||
      establishment?.slug;

    // Prefer a provided establishmentId (could be numeric id or slug) if available, fallback to slug
    const establishmentIdentifier = establishmentId || establishmentSlug;

    if (!establishmentIdentifier) {
      if (onError)
        onError("Não foi possível determinar establishment para exclusão");
      return;
    }

    // Call the thunk using canonical route with establishment identifier and usage id
    await handleDelete(establishmentIdentifier, undefined, undefined, usageId);
  };

  return {
    deleting,
    error,
    handleDelete,
    handleDeleteUsage,
  };
};

export default useDeleteSubscriptionUsage;
